import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaIdCard } from 'react-icons/fa';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton } from 'Core/components/Button';
import Loader from 'Core/components/Loader';
import { AlertError } from 'Core/components/Alert';
import InfoBox from 'Core/components/InfoBox';
import { formatCurrency } from 'Core/utils/numberFormat';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';

import useExistingAccountSubmit from 'Modules/patientfi/hooks/useExistingAccountSubmit';
import { formatAccountDate } from 'Modules/patientfi/utils/accountDate';
import { stripErrorCodes } from 'Modules/patientfi/utils/errorCodes';

export const ACTIONS = {
  CONTINUE: 'CONTINUE',
  REQUEST_INCREASE: 'REQUEST_INCREASE',
  REAPPLY: 'REAPPLY',
};

const WARNING = 'WARNING';

const maskAccountNumber = (accountNumber) =>
  accountNumber ? `•••• ${String(accountNumber).slice(-4)}` : null;

const money = (value) => (typeof value === 'number' ? formatCurrency(value) : null);

const DetailRow = ({ label, value, testId }) =>
  !value ? null : (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-gray-900" data-testid={testId}>
        {value}
      </span>
    </div>
  );

function getDescription(t, accountStatus, actions, increaseAmount) {
  if (accountStatus === 'DENIED') {
    return t(
      'patientfi.existingAccount.description.reapply',
      'Your previous PatientFi application was declined, and you can now reapply. If you reapply and are approved, you can use your PatientFi credit for this purchase.'
    );
  }
  if (actions.includes(ACTIONS.REQUEST_INCREASE) && !actions.includes(ACTIONS.CONTINUE)) {
    return t(
      'patientfi.existingAccount.description.increaseRequired',
      'Your available PatientFi credit is below the minimum financing amount. To use PatientFi for this purchase, ask PatientFi to increase your limit to cover the full amount of {{amount}}. Asking for an increase is a soft credit check.',
      { amount: increaseAmount }
    );
  }
  if (actions.includes(ACTIONS.REQUEST_INCREASE)) {
    return t(
      'patientfi.existingAccount.description.increaseOptional',
      'Your available PatientFi credit does not cover the full purchase. You can continue with your current limit and pay the rest as a down payment, or ask PatientFi to increase your limit to cover the full amount of {{amount}}. Asking for an increase is a soft credit check.',
      { amount: increaseAmount }
    );
  }
  return t(
    'patientfi.existingAccount.description.continue',
    'You already have an approved PatientFi credit line. You can use it for this purchase without a new application.'
  );
}

export default function FinancingExistingAccount({ application = {} }) {
  const { t } = useTranslation();
  document.title = t('patientfi.existingAccount.pageTitle', 'Payment Portal - PatientFi Account');
  useScrollTop();

  const {
    payload: {
      existing_account: existingAccount = {},
      financing_down_payment: financingDownPayment,
      already_paid_amount: alreadyPaidAmount = 0,
      correction_required: correctionRequired,
    } = {},
  } = application;
  const {
    account_number: accountNumber,
    account_status: accountStatus,
    approved_amount: approvedAmount,
    available_balance: availableBalance,
    reapply_date: reapplyDate,
    matched_by: matchedBy,
    available_actions: actions = [],
  } = existingAccount || {};

  const { data: { amount_to_pay: amountToPay } = {} } = useGetPaymentInfo();
  const { submit, submitError, busy } = useExistingAccountSubmit();

  const canContinue = actions.includes(ACTIONS.CONTINUE);
  const canIncrease = actions.includes(ACTIONS.REQUEST_INCREASE);
  const isWarning = correctionRequired?.status === WARNING;

  const matchedByLabel =
    matchedBy === 'EMAIL'
      ? t(
          'patientfi.existingAccount.matchedBy.email',
          'We found this account using your email address.'
        )
      : t(
          'patientfi.existingAccount.matchedBy.phone',
          'We found this account using your phone number.'
        );

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="patientfi"
    >
      {busy && (
        <Loader
          message={t(
            'patientfi.existingAccount.loading',
            'Checking your PatientFi account. This can take a moment.'
          )}
        />
      )}

      <div className="max-w-4xl mx-auto mt-6">
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          <div className="bg-gradient-to-r from-sky-600 to-teal-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaIdCard className="text-white text-xl" />
              </div>
              <div>
                <h1
                  className="text-xl font-semibold text-white"
                  data-testid="title-existing-account"
                >
                  {t('patientfi.existingAccount.title', 'We found your PatientFi account')}
                </h1>
                <p className="text-sky-100 text-sm">{matchedByLabel}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6" data-testid="existing-account-description">
              {getDescription(
                t,
                accountStatus,
                actions,
                typeof amountToPay === 'number'
                  ? money(Math.max(amountToPay - alreadyPaidAmount, 0))
                  : t('patientfi.existingAccount.thisPurchase', 'this purchase')
              )}
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 mb-6">
              <DetailRow
                label={t('patientfi.existingAccount.accountNumber', 'Account number')}
                value={maskAccountNumber(accountNumber)}
                testId="existing-account-number"
              />
              {accountStatus === 'APPROVED' && (
                <>
                  <DetailRow
                    label={t('patientfi.existingAccount.approvedAmount', 'Approved credit line')}
                    value={money(approvedAmount)}
                    testId="existing-account-approved"
                  />
                  <DetailRow
                    label={t('patientfi.existingAccount.availableBalance', 'Available credit')}
                    value={money(availableBalance)}
                    testId="existing-account-available"
                  />
                  {actions.includes(ACTIONS.CONTINUE) && (
                    <DetailRow
                      label={t('patientfi.existingAccount.downPayment', 'Down payment due today')}
                      value={money(financingDownPayment)}
                      testId="existing-account-down-payment"
                    />
                  )}
                </>
              )}
              {accountStatus === 'DENIED' && (
                <DetailRow
                  label={t('patientfi.existingAccount.reapplyDate', 'Eligible to reapply since')}
                  value={formatAccountDate(reapplyDate)}
                  testId="existing-account-reapply-date"
                />
              )}
            </div>

            {isWarning && correctionRequired.message && !submitError && (
              <div className="mb-4" data-testid="existing-account-warning">
                <InfoBox type="warningLite" showBorder>
                  {stripErrorCodes(correctionRequired.message)}
                </InfoBox>
              </div>
            )}

            {!isWarning && correctionRequired?.message && !submitError && (
              <AlertError className="mb-4" data-testid="existing-account-previous-error">
                {t(
                  'patientfi.existingAccount.error.previousAttemptFailed',
                  'We could not finish this step with PatientFi. Please try again.'
                )}
              </AlertError>
            )}

            {submitError && (
              <AlertError className="mb-4" data-testid="existing-account-error">
                {submitError}
              </AlertError>
            )}

            <InfoBox type="message" showBorder>
              {t(
                'patientfi.existingAccount.consent',
                'By continuing, you agree that we link this PatientFi account to your purchase with us.'
              )}
            </InfoBox>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              {canContinue && (
                <PrimaryButton
                  data-testid="btn-existing-account-continue"
                  disabled={busy}
                  onClick={() => submit(false)}
                >
                  {canIncrease
                    ? t(
                        'patientfi.existingAccount.continueCurrentLimitButton',
                        'Continue with current limit'
                      )
                    : t('patientfi.existingAccount.continueButton', 'Continue with this account')}
                </PrimaryButton>
              )}
              {canIncrease && (
                <PrimaryButton
                  data-testid="btn-existing-account-increase"
                  disabled={busy}
                  onClick={() => submit(true)}
                >
                  {t(
                    'patientfi.existingAccount.continueIncreasedLimitButton',
                    'Continue with increased limit'
                  )}
                </PrimaryButton>
              )}
              {actions.includes(ACTIONS.REAPPLY) && (
                <PrimaryButton
                  data-testid="btn-existing-account-reapply"
                  disabled={busy}
                  onClick={() => submit(false)}
                >
                  {t('patientfi.existingAccount.reapplyButton', 'Reapply with this account')}
                </PrimaryButton>
              )}
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
